import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { US_STATES as US_STATES_RAW } from "../components/universal-audit/constants";
import { fetchAINews } from "../lib/aiNewsFeed";
import { getActiveBusinessId, readBusinessPayload } from "../lib/businessStore";
import { buildNewsCoverage, matchesNewsCoverage } from "../lib/aiNewsCoverage";

/**
 * AI News – Phase 6F
 * - Uses live backend feed
 * - Adds a prominent loading state for first-load confidence
 * - Adds a subtle but visible count indicator on the title row
 * - Preserves existing remediation-oriented card structure
 *
 * © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
 */

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function decodeHtmlEntities(value) {
  const raw = String(value || "");
  if (!raw) return "";

  if (typeof window !== "undefined" && typeof window.DOMParser !== "undefined") {
    try {
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(`<!doctype html><body>${raw}`, "text/html");
      return doc?.body?.textContent || raw;
    } catch {
      return raw;
    }
  }

  return raw
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
}

function cleanDisplayText(value, fallback = "") {
  const raw = String(value || "");
  if (!raw.trim()) return fallback;

  const withoutTags = raw
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ");

  const decoded = decodeHtmlEntities(withoutTags);

  const cleaned = decoded
    .replace(/\s+/g, " ")
    .replace(/\s+([,.;:!?])/g, "$1")
    .replace(/([\[(])\s+/g, "$1")
    .replace(/\s+([\])])/g, "$1")
    .trim();

  return cleaned || fallback;
}

function sanitizeNewsItem(item) {
  const source = item && typeof item === "object" ? item : {};
  return {
    ...source,
    title: cleanDisplayText(source?.title, "Untitled update"),
    summary: cleanDisplayText(source?.summary, "No summary available."),
    category: cleanDisplayText(source?.category, "Uncategorized"),
    jurisdiction: cleanDisplayText(source?.jurisdiction, "N/A"),
    severity: cleanDisplayText(source?.severity, "Unknown"),
    source_name: cleanDisplayText(source?.source_name, ""),
    source_url: String(source?.source_url || source?.source || "").trim(),
    scope: cleanDisplayText(source?.scope, "").toUpperCase(),
    state_code: cleanDisplayText(source?.state_code, "").toUpperCase(),
  };
}

const STATE_NAME_BY_CODE = new Map(
  safeArray(US_STATES_RAW).map(([code, name]) => [
    String(code || "").toUpperCase(),
    String(name || code || "").trim(),
  ])
);

function readActiveNewsCoverage() {
  const activeBusinessId = getActiveBusinessId();
  const payload = activeBusinessId ? readBusinessPayload(activeBusinessId) : null;
  return buildNewsCoverage(payload);
}

function getAuditJurisdictionLabel(coverage) {
  if (!coverage?.personalized) return "U.S. Federal (limited view)";

  const labels = [];
  if (coverage.jurisdictions.has("US_FED")) labels.push("U.S. Federal");

  if (coverage.jurisdictions.has("US_STATE")) {
    const stateLabels = Array.from(coverage.selectedStates)
      .sort()
      .map((code) => STATE_NAME_BY_CODE.get(code) || code);
    if (stateLabels.length) labels.push(...stateLabels);
    else if (!coverage.jurisdictions.has("US_FED")) labels.push("U.S. Federal");
  }

  if (coverage.jurisdictions.has("UK")) labels.push("United Kingdom");
  if (coverage.jurisdictions.has("EU")) labels.push("European Union");

  return Array.from(new Set(labels)).join(", ") || "U.S. Federal (limited view)";
}

function getStateMonitoringNote(coverage) {
  if (!coverage?.personalized || !coverage.jurisdictions.has("US_STATE")) return "";

  const selected = coverage.selectedStates || new Set();
  const monitoredSelectedStates = [];
  if (selected.has("CO")) monitoredSelectedStates.push("Colorado");

  if (monitoredSelectedStates.length) {
    return `State-specific AI News monitoring currently active for: ${monitoredSelectedStates.join(", ")}. Other selected states remain audit-profile filters until a validated state source is activated.`;
  }

  return "No selected state currently has an activated state-specific AI News source. Selected states remain audit-profile filters until a validated state source is activated.";
}

function formatDate(dateString) {
  if (!dateString) return "Unknown date";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function isActiveNewsItem(item) {
  const raw = String(item?.date || "").trim();
  if (!raw) return false;

  const publishedAt = new Date(`${raw.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(publishedAt.getTime())) return false;

  const now = new Date();
  const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const cutoffUtc = todayUtc - 90 * 24 * 60 * 60 * 1000;
  const publishedUtc = publishedAt.getTime();

  return publishedUtc >= cutoffUtc && publishedUtc <= todayUtc;
}

function getSourceLabel(item) {
  const explicit = String(item?.source_name || "").trim();
  if (explicit) return explicit;

  const source = String(item?.source_url || item?.source || "").trim();
  if (!source) return "Primary Source";

  try {
    const url = new URL(source);
    const host = url.hostname.replace(/^www\./, "").toLowerCase();

    if (host.includes("ftc.gov")) return "FTC";
    if (host.includes("sec.gov")) return "SEC";
    if (host.includes("dol.gov")) return "DOL";
    if (host.includes("hhs.gov")) return "HHS";
    if (host.includes("justice.gov")) return "DOJ";
    if (host.includes("finra.org")) return "FINRA";
    if (host.includes("treasury.gov")) return "OFAC";
    if (host.includes("cftc.gov")) return "CFTC";
    if (host.includes("nlrb.gov")) return "NLRB";
    if (host.includes("federalreserve.gov")) return "Federal Reserve";
    if (host.includes("occ.treas.gov")) return "OCC";
    if (host.includes("fdic.gov")) return "FDIC";
    if (host.includes("fcc.gov")) return "FCC";
    if (host.includes("ico.org.uk")) return "ICO";
    if (host.includes("cnil.fr")) return "CNIL";
    if (host.includes("europa.eu")) return "EDPB";
    if (host.includes("ncsc.gov.uk")) return "NCSC";
    if (host.includes("gov.uk")) return "GOV.UK";

    return host;
  } catch {
    return "Primary Source";
  }
}

function getSignalType(item) {
  const category = String(item?.category || "").toLowerCase();
  const title = String(item?.title || "").toLowerCase();
  const summary = String(item?.summary || "").toLowerCase();
  const combined = `${category} ${title} ${summary}`;

  if (
    combined.includes("enforcement") ||
    combined.includes("investigation") ||
    combined.includes("penalty") ||
    combined.includes("bias") ||
    combined.includes("discrimination") ||
    combined.includes("charges") ||
    combined.includes("settlement") ||
    combined.includes("sanction")
  ) {
    return "Enforcement";
  }

  if (
    combined.includes("act") ||
    combined.includes("regulatory framework") ||
    combined.includes("regulation") ||
    combined.includes("compliance expectations")
  ) {
    return "Regulation";
  }

  if (
    combined.includes("guidance") ||
    combined.includes("governance") ||
    combined.includes("best practice")
  ) {
    return "Guidance";
  }

  if (
    combined.includes("court") ||
    combined.includes("judgment") ||
    combined.includes("tribunal")
  ) {
    return "Court / Judgment";
  }

  return "Regulatory Update";
}

function getImpactedRiskArea(item) {
  const text = `${item?.title} ${item?.summary} ${item?.category}`.toLowerCase();

  if (text.includes("bias") || text.includes("discrimination")) return "Discrimination Risk";
  if (text.includes("transparency")) return "Transparency Risk";
  if (text.includes("governance")) return "Governance Failure Risk";
  if (text.includes("privacy") || text.includes("data protection") || text.includes("hipaa")) {
    return "Privacy Risk";
  }
  if (text.includes("security") || text.includes("cyber") || text.includes("breach")) {
    return "Security Risk";
  }

  return "General AI Compliance Risk";
}

function getLinkedRiskCategory(item) {
  const impactedRiskArea = getImpactedRiskArea(item);

  if (impactedRiskArea === "Discrimination Risk") return "Bias / Discrimination Exposure";
  if (impactedRiskArea === "Transparency Risk") return "Disclosure / Transparency Exposure";
  if (impactedRiskArea === "Governance Failure Risk") return "Governance and Control Failure Exposure";
  if (impactedRiskArea === "Privacy Risk") return "Data Protection / Privacy Exposure";
  if (impactedRiskArea === "Security Risk") return "Security / Breach Exposure";

  return "General Regulatory Exposure";
}

function getConfidence(item) {
  const sourceName = String(item?.source_name || "").toLowerCase();
  const source = String(item?.source_url || item?.source || "").toLowerCase();
  const combined = `${sourceName} ${source}`;

  if (
    combined.includes("ftc") ||
    combined.includes("sec") ||
    combined.includes("dol") ||
    combined.includes("hhs") ||
    combined.includes("doj") ||
    combined.includes("finra") ||
    combined.includes("ofac") ||
    combined.includes("cftc") ||
    combined.includes("nlrb") ||
    combined.includes("federal reserve") ||
    combined.includes("occ") ||
    combined.includes("fdic") ||
    combined.includes("fcc") ||
    combined.includes("ico") ||
    combined.includes("cnil") ||
    combined.includes("edpb") ||
    combined.includes("ncsc") ||
    combined.includes("gov.uk")
  ) {
    return "High";
  }

  return "Medium";
}

function getConfidenceLabel(item) {
  return getConfidence(item) === "High" ? "Regulatory Source" : "Secondary Source";
}

function getWhyItMatters(item) {
  const jurisdiction = String(item?.jurisdiction || "relevant jurisdiction");
  const category = String(item?.category || "AI compliance");
  const type = getSignalType(item);

  if (type === "Enforcement") {
    return `This update may increase enforcement risk in ${jurisdiction}, particularly for organizations dealing with ${category.toLowerCase()} issues where governance or documentation controls may be weak.`;
  }

  if (type === "Regulation") {
    return `This update raises compliance expectations in ${jurisdiction}. It may require closer attention if your controls, notices, or operating procedures are not well aligned.`;
  }

  if (type === "Guidance") {
    return `This update helps show how regulators expect governance to operate in ${jurisdiction}, which may affect defensibility and audit readiness.`;
  }

  if (type === "Court / Judgment") {
    return `This update may influence how legal standards are interpreted in ${jurisdiction}, which can affect defensibility and future control design decisions.`;
  }

  return `This update may affect AI compliance posture in ${jurisdiction}, particularly in the area of ${category.toLowerCase()}.`;
}

function getSuggestedRemediation(item) {
  const linkedRiskCategory = getLinkedRiskCategory(item);

  if (linkedRiskCategory === "Bias / Discrimination Exposure") {
    return [
      "Run a targeted bias and outcome review on affected models or workflows.",
      "Validate governance approvals, testing records, and monitoring evidence.",
      "Document corrective controls for discriminatory outputs, escalation paths, and ownership.",
    ];
  }

  if (linkedRiskCategory === "Disclosure / Transparency Exposure") {
    return [
      "Review required notices, disclosures, and user-facing transparency language.",
      "Validate whether documentation and support scripts reflect actual system behavior.",
      "Capture screenshots or records showing compliant disclosure in practice.",
    ];
  }

  if (linkedRiskCategory === "Governance and Control Failure Exposure") {
    return [
      "Review governance documentation, accountability assignments, and approval workflows.",
      "Confirm that risk review, change control, and oversight checkpoints are operating in practice.",
      "Preserve evidence showing control ownership, review cadence, and exception handling.",
    ];
  }

  if (linkedRiskCategory === "Data Protection / Privacy Exposure") {
    return [
      "Review data flows, retention, lawful-use rationale, and access controls.",
      "Validate privacy documentation such as DPIA-style records or equivalent assessments.",
      "Preserve vendor, security, and user-rights handling evidence.",
    ];
  }

  if (linkedRiskCategory === "Security / Breach Exposure") {
    return [
      "Review security monitoring, incident response, and access governance in the affected workflow.",
      "Validate breach handling records, escalation paths, and audit logs.",
      "Confirm that technical and procedural safeguards are documented and operating in practice.",
    ];
  }

  return [
    "Review the affected control area against current regulatory expectations.",
    "Confirm documentation, approvals, and monitoring evidence are current.",
    "Assign an owner and record follow-up actions for defensibility.",
  ];
}

function getPotentialFineExposureImpact(item) {
  const signalType = getSignalType(item);
  const severity = String(item?.severity || "").toUpperCase();
  const jurisdiction = String(item?.jurisdiction || "relevant jurisdiction");
  const linkedRiskCategory = getLinkedRiskCategory(item);

  if (signalType === "Enforcement" && severity === "HIGH") {
    return {
      headline: "Higher exposure sensitivity",
      detail: `This update may justify a higher enforcement-probability weighting in ${jurisdiction}, especially for ${linkedRiskCategory.toLowerCase()} scenarios.`,
    };
  }

  if (signalType === "Regulation") {
    return {
      headline: "Raised compliance threshold",
      detail: `This update may increase expected exposure where ${jurisdiction} requirements now demand stronger documentation, notices, or operating controls.`,
    };
  }

  if (signalType === "Guidance") {
    return {
      headline: "Stronger audit expectations",
      detail: `This update may increase scrutiny during review or investigation if your governance evidence is incomplete or inconsistent with regulator guidance.`,
    };
  }

  return {
    headline: "Moderate exposure relevance",
    detail: "This update may influence how severity, defensibility, or enforcement posture is interpreted, depending on your controls and documentation.",
  };
}

function getRelevantToYourAudit(item) {
  const linkedRiskCategory = getLinkedRiskCategory(item);
  const jurisdiction = String(item?.jurisdiction || "relevant jurisdiction");
  const severity = String(item?.severity || "Medium").toLowerCase();

  return `This is most relevant if your audit includes ${linkedRiskCategory.toLowerCase()}, operations in ${jurisdiction}, or similar ${severity}-severity control concerns. It helps highlight areas that may need closer attention.`;
}

function getRiskPriorityScore(item) {
  const severity = String(item?.severity || "").toUpperCase();
  const signalType = getSignalType(item);
  const confidence = getConfidence(item);

  let score = 0;
  if (severity === "HIGH") score += 3;
  else if (severity === "MEDIUM") score += 2;
  else score += 1;

  if (signalType === "Enforcement") score += 3;
  else if (signalType === "Regulation") score += 2;
  else if (signalType === "Guidance") score += 1;

  if (confidence === "High") score += 2;

  return score;
}


function getSeverityScoreNumber(item) {
  const raw = Number(item?.severity_score);
  if (Number.isFinite(raw)) {
    return Math.max(0, Math.min(100, Math.round(raw)));
  }

  return Math.min(getRiskPriorityScore(item) * 10, 100);
}

function getSortOptions() {
  return [
    { value: "mostImportant", label: "Most Important" },
    { value: "latest", label: "Latest" },
  ];
}

function getSpotlightSummary(item) {
  const linkedRiskCategory = getLinkedRiskCategory(item);
  const jurisdiction = String(item?.jurisdiction || "relevant jurisdiction");
  return `This is currently the most important update because it combines ${String(item?.severity || "elevated").toLowerCase()} severity with ${getSignalType(item).toLowerCase()} relevance in ${jurisdiction}, directly affecting ${linkedRiskCategory.toLowerCase()}.`;
}

function jurisdictionTone(jurisdiction) {
  const key = String(jurisdiction || "").toUpperCase();
  if (key === "US") return "border-blue-200 bg-blue-50 text-blue-700";
  if (key === "EU") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (key === "UK") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-slate-200 bg-slate-50 text-slate-700";
}

function severityTone(severity) {
  const key = String(severity || "").toUpperCase();
  if (key === "HIGH") return "border-red-200 bg-red-50 text-red-700";
  if (key === "MEDIUM") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-slate-200 bg-slate-50 text-slate-700";
}

function severityScoreTone(score) {
  if (score >= 80) return "border-red-200 bg-red-50 text-red-700";
  if (score >= 60) return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-blue-200 bg-blue-50 text-blue-700";
}

function confidenceTone(confidence) {
  const key = String(confidence || "").toUpperCase();
  if (key === "HIGH") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  return "border-amber-200 bg-amber-50 text-amber-700";
}

function cardTone(item) {
  const severity = String(item?.severity || "").toUpperCase();
  const type = getSignalType(item);

  if (severity === "HIGH" && type === "Enforcement") {
    return "border-red-200 bg-red-50/40";
  }

  if (severity === "HIGH") {
    return "border-amber-200 bg-amber-50/30";
  }

  return "border-slate-200 bg-slate-50";
}

function CountIndicator({ count }) {
  return (
    <div className="inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm">
      {count} Active Regulatory Updates
    </div>
  );
}

function LoadingState() {
  return (
    <div className="mt-8 rounded-3xl border border-blue-200 bg-white p-8 shadow-sm">
      <div className="flex flex-col items-center justify-center text-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
        <div className="mt-6 text-xl font-semibold text-slate-900">
          Gathering the latest regulatory updates...
        </div>
        <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600">
          This can take a couple of minutes while the page gathers recent updates from multiple regulators.
        </p>
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="mt-8 rounded-3xl border border-red-200 bg-white p-8 shadow-sm">
      <div className="text-lg font-semibold text-slate-900">Unable to load AI News</div>
      <p className="mt-3 text-sm leading-6 text-slate-600">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-5 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50"
      >
        Retry
      </button>
    </div>
  );
}

export default function AINews() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const [jurisdictionFilter, setJurisdictionFilter] = useState("All");
  const [severityFilter, setSeverityFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [sortMode, setSortMode] = useState("mostImportant");
  const coverage = useMemo(() => readActiveNewsCoverage(), []);
  const coverageLabel = useMemo(() => getAuditJurisdictionLabel(coverage), [coverage]);
  const stateMonitoringNote = useMemo(() => getStateMonitoringNote(coverage), [coverage]);

  const loadNews = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const payload = await fetchAINews({ limit: 100 });
      const nextItems = safeArray(payload)
        .map((item) => sanitizeNewsItem(item))
        .filter((item) => isActiveNewsItem(item))
        .slice()
        .sort((a, b) => {
          const aTime = new Date(a?.date || 0).getTime();
          const bTime = new Date(b?.date || 0).getTime();
          return bTime - aTime;
        });

      setItems(nextItems);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load AI News.");
    } finally {
      setLoading(false);
      setInitialLoadComplete(true);
    }
  }, []);

  useEffect(() => {
    loadNews();
  }, [loadNews]);

  const jurisdictions = useMemo(() => ["All", "US", "UK", "EU"], []);

  const coverageItems = useMemo(
    () => items.filter((item) => matchesNewsCoverage(item, coverage)),
    [items, coverage]
  );

  const severities = useMemo(() => {
    return ["All", ...Array.from(new Set(coverageItems.map((item) => item?.severity).filter(Boolean)))];
  }, [coverageItems]);

  const categories = useMemo(() => {
    return ["All", ...Array.from(new Set(coverageItems.map((item) => item?.category).filter(Boolean)))];
  }, [coverageItems]);

  const sortOptions = useMemo(() => getSortOptions(), []);

  const filteredItems = useMemo(() => {
    return coverageItems.filter((item) => {
      const jurisdictionMatch =
        jurisdictionFilter === "All" || item?.jurisdiction === jurisdictionFilter;
      const severityMatch = severityFilter === "All" || item?.severity === severityFilter;
      const categoryMatch = categoryFilter === "All" || item?.category === categoryFilter;

      return jurisdictionMatch && severityMatch && categoryMatch;
    });
  }, [coverageItems, jurisdictionFilter, severityFilter, categoryFilter]);

  const displayedItems = useMemo(() => {
    const nextItems = filteredItems.slice();

    nextItems.sort((a, b) => {
      const aTime = new Date(a?.date || 0).getTime();
      const bTime = new Date(b?.date || 0).getTime();
      const aSeverityScore = getSeverityScoreNumber(a);
      const bSeverityScore = getSeverityScoreNumber(b);
      const aPriority = getRiskPriorityScore(a);
      const bPriority = getRiskPriorityScore(b);

      if (sortMode === "latest") {
        if (bTime !== aTime) return bTime - aTime;
        if (bSeverityScore !== aSeverityScore) return bSeverityScore - aSeverityScore;
        return bPriority - aPriority;
      }

      if (bSeverityScore !== aSeverityScore) return bSeverityScore - aSeverityScore;
      if (bPriority !== aPriority) return bPriority - aPriority;
      return bTime - aTime;
    });

    return nextItems;
  }, [filteredItems, sortMode]);


  const visibleDisplayLimit = Math.min(displayedItems.length, 12);

  const visibleItems = useMemo(() => displayedItems.slice(0, visibleDisplayLimit), [displayedItems, visibleDisplayLimit]);

  const highestRiskSignal = useMemo(() => {
    if (!displayedItems.length) return null;

    return displayedItems
      .slice()
      .sort((a, b) => {
        const severityDelta = getSeverityScoreNumber(b) - getSeverityScoreNumber(a);
        if (severityDelta !== 0) return severityDelta;

        const priorityDelta = getRiskPriorityScore(b) - getRiskPriorityScore(a);
        if (priorityDelta !== 0) return priorityDelta;

        return new Date(b?.date || 0).getTime() - new Date(a?.date || 0).getTime();
      })[0];
  }, [displayedItems]);

  const resetFilters = () => {
    setJurisdictionFilter("All");
    setSeverityFilter("All");
    setCategoryFilter("All");
  };

  const goToRemediation = (item) => {
    const category = encodeURIComponent(getLinkedRiskCategory(item));
    navigate(`/remediation?newsRisk=${category}`);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 text-slate-900">
      <Sidebar />

      <main className="flex-1 overflow-auto">
        <div className="mx-auto max-w-6xl px-8 py-10">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="mt-4 flex flex-wrap items-center gap-4">
                <h1 className="text-4xl font-bold tracking-tight text-slate-900">AI News</h1>
                {initialLoadComplete && !loading ? <CountIndicator count={filteredItems.length} /> : null}
              </div>

              <p className="mt-2 max-w-3xl text-sm text-slate-600">
                Curated regulatory, enforcement, and policy updates relevant to AI compliance exposure.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
<button
                type="button"
                onClick={() => navigate("/reports")}
                className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50"
              >
                View Reports
              </button>
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-3 rounded-2xl border border-blue-100 bg-blue-50/60 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-sm font-black text-blue-950">
                Audit profile jurisdictions: {coverageLabel}
              </div>
              <div className="mt-1 text-xs leading-5 text-blue-800">
                {!coverage.personalized
                  ? "AI News is limited to U.S. Federal updates because no authoritative active audit profile was found. Update your audit profile to tailor the feed."
                  : coverage.stateSelectionIncomplete
                  ? "Your U.S. State selection is incomplete, so state-specific news is withheld and U.S. Federal updates are shown instead."
                  : "AI News is filtered to the jurisdictions in your current audit profile."}
              </div>
              {stateMonitoringNote ? (
                <div className="mt-1 text-xs leading-5 text-blue-800">
                  {stateMonitoringNote}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => navigate("/start-audit?mode=rerun")}
              className="shrink-0 rounded-xl border border-blue-200 bg-white px-4 py-2 text-xs font-black text-blue-800 shadow-sm transition hover:bg-blue-50"
            >
              Update Jurisdictions →
            </button>
          </div>

          {loading && !initialLoadComplete ? <LoadingState /> : null}
          {!loading && error ? <ErrorState message={error} onRetry={loadNews} /> : null}

          {!loading && !error ? (
            <div className="flex flex-col">
              {highestRiskSignal ? (
                <div className="order-2 mt-8 rounded-3xl border border-red-200 bg-red-50/50 p-6 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="text-[11px] font-black uppercase tracking-[0.22em] text-red-700">
                        {jurisdictionFilter === "All"
                          ? "Highest Risk Update"
                          : `Highest Risk ${jurisdictionFilter} Update`}
                      </div>
                      <h2 className="mt-3 text-2xl font-semibold text-slate-900">
                        {highestRiskSignal.title}
                      </h2>
                      <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-700">
                        {getSpotlightSummary(highestRiskSignal)}
                      </p>
                      <div className="mt-3 inline-flex rounded-full border border-red-200 bg-white px-3 py-1 text-xs font-semibold text-red-700">
                        Priority Score {getSeverityScoreNumber(highestRiskSignal)}/100
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => goToRemediation(highestRiskSignal)}
                      className="rounded-2xl border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50"
                    >
                      Go to Remediation
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="order-1 mt-8 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-semibold text-slate-900">Filters</h2>
                    <p className="mt-1 text-sm text-slate-500">
                      Narrow updates by jurisdiction, severity, or category.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={resetFilters}
                    className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    Reset Filters
                  </button>
                </div>

                <div className="mt-6 grid gap-4 md:grid-cols-4">
                  <div>
                    <label className="mb-2 block text-xs font-black uppercase tracking-[0.22em] text-slate-500">
                      Jurisdiction
                    </label>
                    <select
                      value={jurisdictionFilter}
                      onChange={(e) => setJurisdictionFilter(e.target.value)}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none"
                    >
                      {jurisdictions.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-2 block text-xs font-black uppercase tracking-[0.22em] text-slate-500">
                      Severity
                    </label>
                    <select
                      value={severityFilter}
                      onChange={(e) => setSeverityFilter(e.target.value)}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none"
                    >
                      {severities.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-2 block text-xs font-black uppercase tracking-[0.22em] text-slate-500">
                      Category
                    </label>
                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none"
                    >
                      {categories.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-2 block text-xs font-black uppercase tracking-[0.22em] text-slate-500">
                      Sort By
                    </label>
                    <select
                      value={sortMode}
                      onChange={(e) => setSortMode(e.target.value)}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none"
                    >
                      {sortOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="order-3 mt-8 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-semibold text-slate-900">
                      {sortMode === "mostImportant" ? "Most Important Updates" : "Latest Updates"}
                    </h2>
                    <p className="mt-1 text-sm text-slate-500">
                      Only regulatory, enforcement, governance, and compliance-relevant updates appear here.
                    </p>
                  </div>

                  <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                    Showing {visibleItems.length} of {filteredItems.length} matching updates · Automatically showing up to 12 · {sortMode === "mostImportant" ? "Sorted by importance" : "Sorted by latest"}
                  </div>
                </div>

                {visibleItems.length === 0 ? (
                  <div className="mt-6 rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">
                    No AI regulatory updates match your current filters.
                  </div>
                ) : (
                  <div className="mt-6 space-y-4">
                    {visibleItems.map((item) => {
                      const sourceLabel = getSourceLabel(item);
                      const signalType = getSignalType(item);
                      const whyItMatters = getWhyItMatters(item);
                      const impactedRiskArea = getImpactedRiskArea(item);
                      const linkedRiskCategory = getLinkedRiskCategory(item);
                      const suggestedRemediation = getSuggestedRemediation(item);
                      const exposureImpact = getPotentialFineExposureImpact(item);
                      const relevance = getRelevantToYourAudit(item);
                      const confidence = getConfidence(item);
                      const sourceUrl = item?.source_url || item?.source || "#";

                      return (
                        <div key={item.id} className={`rounded-3xl border p-5 ${cardTone(item)}`}>
                          <div className="flex flex-wrap items-center gap-2">
                            <span
                              className={`rounded-full border px-3 py-1 text-xs font-semibold ${jurisdictionTone(
                                item?.jurisdiction
                              )}`}
                            >
                              {item?.jurisdiction || "N/A"}
                            </span>

                            <span
                              className={`rounded-full border px-3 py-1 text-xs font-semibold ${severityTone(
                                item?.severity
                              )}`}
                            >
                              {item?.severity || "Unknown"}
                            </span>

                            <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600">
                              {item?.category || "Uncategorized"}
                            </span>

                            <span
                              className={`rounded-full border px-3 py-1 text-xs font-semibold ${severityScoreTone(
                                getSeverityScoreNumber(item)
                              )}`}
                            >
                              Priority Score {getSeverityScoreNumber(item)}/100
                            </span>

                            <span className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
                              {signalType}
                            </span>

                            <span
                              className={`rounded-full border px-3 py-1 text-xs font-semibold ${confidenceTone(
                                confidence
                              )}`}
                            >
                              {getConfidenceLabel(item)}
                            </span>
                          </div>

                          <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
                            <div className="min-w-0 flex-1">
                              <h3 className="text-xl font-semibold text-slate-900">{item?.title}</h3>
                            </div>

                            <button
                              type="button"
                              onClick={() => goToRemediation(item)}
                              className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50"
                            >
                              Go to Remediation
                            </button>
                          </div>

                          <p className="mt-3 text-sm leading-6 text-slate-600">{item?.summary}</p>

                          <div className="mt-4 grid gap-4 md:grid-cols-2">
                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                              <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">
                                Impacted Risk Area
                              </div>
                              <p className="mt-2 text-sm leading-6 text-slate-700">{impactedRiskArea}</p>
                            </div>

                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                              <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">
                                Linked Risk Category
                              </div>
                              <p className="mt-2 text-sm leading-6 text-slate-700">{linkedRiskCategory}</p>
                            </div>
                          </div>

                          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
                            <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">
                              Why it matters
                            </div>
                            <p className="mt-2 text-sm leading-6 text-slate-700">{whyItMatters}</p>
                          </div>

                          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
                            <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">
                              Suggested Remediation
                            </div>
                            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-6 text-slate-700">
                              {suggestedRemediation.map((step, index) => (
                                <li key={`${item.id}_rem_${index}`}>{step}</li>
                              ))}
                            </ul>
                          </div>

                          <div className="mt-4 grid gap-4 md:grid-cols-2">
                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                              <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">
                                Potential Fine Exposure Impact
                              </div>
                              <div className="mt-2 text-sm font-semibold text-slate-900">
                                {exposureImpact.headline}
                              </div>
                              <p className="mt-2 text-sm leading-6 text-slate-700">{exposureImpact.detail}</p>
                            </div>

                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                              <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">
                                Relevant to Your Audit
                              </div>
                              <p className="mt-2 text-sm leading-6 text-slate-700">{relevance}</p>
                            </div>
                          </div>

                          <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-slate-500">
                            <span>{formatDate(item?.date)}</span>
                            <span className="rounded-full border border-slate-200 bg-white px-2 py-1 font-semibold text-slate-600">
                              {sourceLabel}
                            </span>
                            <a
                              href={sourceUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800"
                            >
                              View Source ↗
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : null}

          <div className="mt-8 text-xs text-slate-400">
            © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
          </div>
        </div>
      </main>
    </div>
  );
}
